import React from 'react';
import { ArrowRight, FileCheck2, Signature, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { Progress } from '../ui/Progress';
import { GateStatusPill } from '../common/StatusPills';
import { AdvanceStageDialog } from './AdvanceStageDialog';
import { summariseGate, STAGE_META, nextStage } from '../../utils/stages';
import type { ApplicationRecord, StageId } from '../../types/domain';
interface GatePanelProps {
  record: ApplicationRecord;
  stage: StageId;
  readOnly?: boolean;
}
function verdict(record: ApplicationRecord, stage: StageId) {
  const items = record.gates[stage] ?? [];
  const summary = summariseGate(items);
  const outstanding = items.filter(item => item.status === 'outstanding');
  if (summary.total === 0) {
    return 'No gate definition is configured for this stage on this record.';
  }
  if (summary.cleared) {
    return `All ${summary.total} gate items are met. The record is recommended to advance to ${nextStage(record.track, stage) ? STAGE_META[nextStage(record.track, stage)!].label : 'closure'}.`;
  }
  if (outstanding.length > 0) {
    return `Gate readiness ${summary.percent}%. ${outstanding.length} item${outstanding.length === 1 ? '' : 's'} outstanding — ${outstanding[0].label.toLowerCase()} is the binding constraint, owned by ${outstanding[0].owner}.`;
  }
  return `Gate readiness ${summary.percent}%. All items are in progress with none outstanding; advancement is recommended once partial items are confirmed.`;
}
export function GatePanel({
  record,
  stage,
  readOnly = false
}: GatePanelProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const items = record.gates[stage] ?? [];
  const summary = summariseGate(items);
  const target = nextStage(record.track, record.stage);
  const isCurrentStage = stage === record.stage;
  return <section className="rounded-sm border border-border bg-card" data-id="element-768" data-mp="35jixl52oek09">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3" data-id="element-769" data-mp="3ajf4pd6o6rk1">
        <div data-id="element-770" data-mp="39548jlaa67so">
          <h2 className="text-[13px] font-semibold tracking-tight" data-id="element-771" data-mp="391gf0m4u5ldy">
            {STAGE_META[stage].label} gate
          </h2>
          <p className="mt-0.5 text-[12px] text-muted-foreground" data-id="element-772" data-mp="2z5dbpcy8zgpf">
            Required artefacts and sign-offs before advancement
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-773" data-mp="2v5cq98cqe58v">
          <div className="w-32" data-id="element-774" data-mp="3rtrb9x4zil33">
            <Progress value={summary.percent} data-id="element-775" data-mp="3rspr935zqo98" />
          </div>
          <span className="font-mono text-[12px] text-muted-foreground" data-id="element-776" data-mp="3mv7ndr6tyz83">{summary.percent}%</span>
        </div>
      </header>

      <div className="grid gap-4 p-4 lg:grid-cols-[1.6fr_1fr]" data-id="element-777" data-mp="3i367lvageqdb">
        <ul className="divide-y divide-border" data-id="element-778" data-mp="3e2w18hir1xxx">
          {items.length === 0 && <li className="py-3 text-[12px] text-muted-foreground" data-id="element-779" data-mp="3e0tatwdoq2so">
              No gate items are configured for this stage.
            </li>}
          {items.map(item => <li key={item.id} className="flex items-start justify-between gap-3 py-2.5 first:pt-0" data-id="element-780" data-mp="9mbgfd30quf2">
              <div className="flex min-w-0 items-start gap-2.5" data-id="element-781" data-mp="1sdiln45piyw1">
                {item.kind === 'signoff' ? <Signature className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" data-id="element-782" data-mp="21buab0n0jmlu" /> : <FileCheck2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" data-id="element-783" data-mp="268kqdnytii79" />}
                <div className="min-w-0" data-id="element-784" data-mp="2g70f5kadhfdl">
                  <p className="text-[13px] leading-snug text-foreground" data-id="element-785" data-mp="2kzl70g9pxieh">{item.label}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground" data-id="element-786" data-mp="2p1ybl1cqba9o">
                    {item.kind === 'signoff' ? 'Sign-off' : 'Artefact'} · {item.owner}
                    {item.note ? ` — ${item.note}` : ''}
                  </p>
                </div>
              </div>
              <GateStatusPill status={item.status} data-id="element-787" data-mp="2lcvwfarjb4oq" />
            </li>)}
        </ul>

        <div className="flex flex-col justify-between gap-3 rounded-sm border-l-4 border-uae-gold bg-uae-navy p-3.5 text-white" data-id="element-788" data-mp="2laj7gdnczu26">
          <div data-id="element-789" data-mp="2v38gl1vfsvuc">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-white/60" data-id="element-790" data-mp="2v1njb4hqmyss">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" data-id="element-791" data-mp="2q4dxdytk559p" />
              AI stage readiness
            </p>
            <p className="mt-2 text-[12px] leading-relaxed text-white/85" data-id="element-792" data-mp="22g47akpb9vcr">
              {verdict(record, stage)}
            </p>
            <dl className="mt-3 grid grid-cols-3 gap-2 text-center" data-id="element-793" data-mp="2cbxmycybu4yr">
              <div className="rounded-sm bg-white/5 py-2" data-id="element-794" data-mp="2c9upj7uuh9k0">
                <dt className="text-[10px] uppercase tracking-wide text-white/50" data-id="element-795" data-mp="27cbc2a5z10t4">Met</dt>
                <dd className="font-mono text-sm text-white" data-id="element-796" data-mp="3nqbafgp64ie9">{summary.met}</dd>
              </div>
              <div className="rounded-sm bg-white/5 py-2" data-id="element-797" data-mp="3ir83fqpmo23n">
                <dt className="text-[10px] uppercase tracking-wide text-white/50" data-id="element-798" data-mp="3qbsffgfuf2i8">Partial</dt>
                <dd className="font-mono text-sm text-white" data-id="element-799" data-mp="3qe56qk7hqp7y">{summary.partial}</dd>
              </div>
              <div className="rounded-sm bg-white/5 py-2" data-id="element-800" data-mp="3hb41elqg925c">
                <dt className="text-[10px] uppercase tracking-wide text-white/50" data-id="element-801" data-mp="3hcowqdkl8p49">Open</dt>
                <dd className="font-mono text-sm text-white" data-id="element-802" data-mp="3ldh5z6tgmu0j">{summary.outstanding}</dd>
              </div>
            </dl>
          </div>

          {!readOnly && isCurrentStage && <div data-id="element-803" data-mp="hw8tz3leyodq">
              <Button size="sm" variant="secondary" className="w-full bg-white text-uae-navy hover:bg-white/90" onClick={() => setDialogOpen(true)} disabled={!target} data-id="element-804" data-mp="hunz1cv6t3fc">
                {summary.cleared ? 'Advance to next stage' : 'Gate blocked — override to advance'}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-805" data-mp="10n096ls31901" />
              </Button>
              <p className="mt-1.5 text-[10px] leading-relaxed text-white/50" data-id="element-806" data-mp="1aiyzezasez6g">
                {target ? summary.cleared ? `Next stage: ${STAGE_META[target].label}` : 'Overrides are attributed and recorded on the audit timeline.' : 'Final stage of the lifecycle.'}
              </p>
            </div>}
          {!isCurrentStage && <p className="text-[10px] leading-relaxed text-white/50" data-id="element-807" data-mp="rkmob80bff88">
              Historic gate — advancement actions are available on the current stage only.
            </p>}
        </div>
      </div>

      <AdvanceStageDialog record={record} open={dialogOpen} onOpenChange={setDialogOpen} gateCleared={summary.cleared} outstandingCount={summary.outstanding + summary.partial} data-id="element-808" data-mp="irsw69zrslcy" />
    </section>;
}